import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';
import IconButton from '@components/IconButton';

const messages = defineMessages({
  title: {
    id: 'admin.configurations.publishConfirmationTitle',
    defaultMessage: 'Publish Release',
  },
  version: {
    id: 'admin.configurations.publishConfirmationVersion',
    defaultMessage: 'Version',
  },
  app: {
    id: 'admin.configurations.publishConfirmationApp',
    defaultMessage: 'App',
  },
  platform: {
    id: 'admin.configurations.publishConfirmationPlatform',
    defaultMessage: 'Platform',
  },
  releaseNotes: {
    id: 'admin.configurations.publishConfirmationReleaseNotes',
    defaultMessage: 'Release notes',
  },
  irreversibleWarning: {
    id: 'admin.configurations.publishIrreversibleWarning',
    defaultMessage:
      'This release cannot be edited or deleted after publishing.',
  },
  minimumWarning: {
    id: 'admin.configurations.publishMinimumWarning',
    defaultMessage:
      'Setting this as the minimum version will force all users below this version to update before they can use the app. This cannot be changed after publishing.',
  },
  cancel: {
    id: 'admin.configurations.cancel',
    defaultMessage: 'Cancel',
  },
  confirmPublish: {
    id: 'admin.configurations.confirmPublish',
    defaultMessage: 'Publish',
  },
  close: {
    id: 'GLOBAL.close',
    defaultMessage: 'Close',
  },
});

export interface PublishConfirmationDialogProps {
  open: boolean;
  versionNumber: string;
  appLabel: string;
  platformLabel: string;
  releaseNotes: string;
  isMinimumVersion: boolean;
  confirming?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function PublishConfirmationDialog({
  open,
  versionNumber,
  appLabel,
  platformLabel,
  releaseNotes,
  isMinimumVersion,
  confirming = false,
  onCancel,
  onConfirm,
}: PublishConfirmationDialogProps) {
  const { formatMessage } = useIntl();
  const { palette } = useTheme();

  return (
    <Dialog
      aria-labelledby="publish-confirmation-title"
      data-testid="publish-confirmation-dialog"
      disableScrollLock
      maxWidth="sm"
      open={open}
      fullWidth
      transitionDuration={0}
      onClose={() => {
        if (confirming) {
          return;
        }
        onCancel();
      }}
    >
      <Stack
        alignItems="center"
        bgcolor="primary.main"
        direction="row"
        justifyContent="space-between"
        px={2}
        py={1.25}
      >
        <Stack alignItems="center" direction="row" spacing={1}>
          <Icon
            color={palette.primary.contrastText}
            name="paper-plane"
            type="fas"
          />
          <Typography
            color="primary.contrastText"
            fontWeight={700}
            id="publish-confirmation-title"
            variant="subtitle1"
          >
            {formatMessage(messages.title)}
          </Typography>
        </Stack>
        <IconButton
          color={palette.primary.contrastText}
          disabled={confirming}
          iconType="fas"
          name="xmark"
          tooltip={formatMessage(messages.close)}
          onClick={onCancel}
        />
      </Stack>

      <DialogContent>
        <Stack spacing={2} pt={1}>
          <Paper
            data-testid="publish-confirmation-summary"
            elevation={0}
            sx={{
              bgcolor: palette.grey[50],
              border: `1px solid ${palette.divider}`,
              borderRadius: 2,
              p: 2,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-between"
            >
              <Stack spacing={0.5}>
                <Typography color="text.secondary" variant="caption">
                  {formatMessage(messages.version)}
                </Typography>
                <Typography
                  color="primary"
                  data-testid="publish-confirmation-version"
                  fontWeight={700}
                  variant="body1"
                >
                  {versionNumber}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography color="text.secondary" variant="caption">
                  {formatMessage(messages.app)}
                </Typography>
                <Typography
                  data-testid="publish-confirmation-app"
                  fontWeight={700}
                  variant="body1"
                >
                  {appLabel}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography color="text.secondary" variant="caption">
                  {formatMessage(messages.platform)}
                </Typography>
                <Typography
                  data-testid="publish-confirmation-platform"
                  fontWeight={700}
                  variant="body1"
                >
                  {platformLabel}
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          {releaseNotes.trim() ? (
            <Stack spacing={0.5}>
              <Typography color="text.secondary" variant="caption">
                {formatMessage(messages.releaseNotes)}
              </Typography>
              <Typography
                data-testid="publish-confirmation-release-notes"
                sx={{ whiteSpace: 'pre-wrap' }}
                variant="body2"
              >
                {releaseNotes.trim()}
              </Typography>
            </Stack>
          ) : null}

          <Stack
            alignItems="flex-start"
            bgcolor="error.light"
            borderRadius={2}
            data-testid="publish-irreversible-warning"
            direction="row"
            spacing={1.5}
            p={1.5}
          >
            <Icon color={palette.error.main} name="circle-xmark" type="fas" />
            <Typography color="error.dark" variant="body2">
              {formatMessage(messages.irreversibleWarning)}
            </Typography>
          </Stack>

          {isMinimumVersion ? (
            <Stack
              alignItems="flex-start"
              bgcolor="warning.light"
              borderRadius={2}
              data-testid="publish-minimum-warning"
              direction="row"
              spacing={1.5}
              p={1.5}
            >
              <Icon
                color={palette.warning.main}
                name="triangle-exclamation"
                type="fas"
              />
              <Typography color="warning.dark" variant="body2">
                {formatMessage(messages.minimumWarning)}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
        <Button
          color="primary"
          data-testid="cancel-publish-button"
          disabled={confirming}
          sx={{ borderRadius: '29px', textTransform: 'none' }}
          type="button"
          variant="outlined"
          onClick={onCancel}
        >
          {formatMessage(messages.cancel)}
        </Button>
        <Button
          color="success"
          data-testid="confirm-publish-button"
          disabled={confirming}
          startIcon={<Icon name="paper-plane" type="fas" />}
          sx={{ borderRadius: '29px', textTransform: 'none' }}
          type="button"
          variant="contained"
          onClick={onConfirm}
        >
          {formatMessage(messages.confirmPublish)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PublishConfirmationDialog;
